import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {DetailsView} from '~/components/DetailsView';
import {useConfirm} from '~/components/Dialogs';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {TableRowActions, TableSort} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {renderBoolean} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {ResourceLoader, useResource} from '~/lib/loader';
import {biddersLinks} from '~/modules/Bidders';
import {PreTargetingAbility} from '~/modules/Bidders/ability';
import {preTargetingVariablesAdapter} from '~/modules/Bidders/adapters';
import {fetchBidder, fetchPreTargetingConfig, updatePreTargetingConfig} from '~/modules/Bidders/api';
import {IPreTargetingForm, IPreTargetingFormActions, PreTargetingForm} from '~/modules/Bidders/forms/PreTargetingForm';
import {PreTargetingRulesEditorForm} from '~/modules/Bidders/forms/PreTargetingRulesEditorForm';
import {IPreTargetingVariableForm, IPreTargetingVariableFormActions, PreTargetingVariableForm} from '~/modules/Bidders/forms/PreTargetingVariableForm';
import {defaultPreTargetingVariable} from '~/modules/Bidders/models/PreTargeting';

interface IPreTargetingConfigViewParams extends Record<string, string | undefined> {
    bidderId: string;
    preTargetingConfigId: string;
}

export const PreTargetingConfigView = () => {
    const confirm = useConfirm();
    const ability = useAbility(PreTargetingAbility);
    const params = useParams<IPreTargetingConfigViewParams>();
    const bidderResult = useResource(() => fetchBidder(params.bidderId));
    const configResult = useResource(() => fetchPreTargetingConfig(params.preTargetingConfigId));
    const variablesSource = useDataSource(preTargetingVariablesAdapter, {primaryKey: 'name'});
    const modals = useModals();

    useEffect(() => {
        variablesSource.fetchRecords(params.preTargetingConfigId);
    }, []);

    const editPreTargeting = modals.create<IPreTargetingForm>(PreTargetingForm, {
        onSubmit: (preTargeting, formActions: IPreTargetingFormActions, close) => {
            updatePreTargetingConfig(preTargeting)
                .then(configResult.setResource)
                .then(close)
                .catch(error => {
                    formActions.setSubmitting(false);
                    formActions.setStatus(getApiErrorMessage(error));
                });
        }
    });

    const editVariable = modals.create<IPreTargetingVariableForm>(PreTargetingVariableForm, {
        onSubmit: (variable, formActions: IPreTargetingVariableFormActions, close) => {
            variablesSource.updateRecord({
                ...variable,
                preTargetingConfigId: params.preTargetingConfigId
            }).then(close).catch(error => {
                formActions.setSubmitting(false);
                formActions.setStatus(getApiErrorMessage(error));
            });
        }
    });

    const createVariable = modals.create<IPreTargetingVariableForm>(PreTargetingVariableForm, {
        onSubmit: (variable, formActions: IPreTargetingVariableFormActions, close) => {
            variablesSource.createRecord({
                ...variable,
                preTargetingConfigId: params.preTargetingConfigId
            }).then(close).catch(error => {
                formActions.setSubmitting(false);
                formActions.setStatus(getApiErrorMessage(error));
            });
        }
    });

    const editRules = modals.create(PreTargetingRulesEditorForm, {});

    return <ResourceLoader result={bidderResult} message='Loading bidder'>
        {({resource: bidder, isPending: isBidderPending}) =>
            <Breadcrumb breadcrumb={{
                to: biddersLinks.bidder(bidder.id),
                title: bidder.title
            }}>
                <Breadcrumb breadcrumb={{
                    title: 'PreTargeting'
                }}>
                    <ResourceLoader result={configResult} message='Loading config'>
                        {({resource: config, isPending: isConfigPending}) => <Breadcrumb breadcrumb={{
                            to: biddersLinks.bidderPreTargetingConfig(bidder.id, config.id),
                            title: config.title
                        }}>
                            <BreadcrumbsResourcePage title={config.title} toolbar={ability.canEdit(config) && <>
                                <Button responsive
                                    key='wizard'
                                    intent={Intent.PRIMARY}
                                    disabled={isConfigPending || isBidderPending}
                                    leftIcon={<i className='fa fa-magic'/>}
                                    to={biddersLinks.bidderPreTargetingWizard(bidder.id, config.id)}>
                                    Wizard
                                </Button>
                                <Button responsive
                                    key='rules'
                                    intent={Intent.WARNING}
                                    disabled={isConfigPending || isBidderPending}
                                    leftIcon={<i className='fa fa-codepen'/>}
                                    onClick={() => editRules.show({preTargetingId: config.id, preTargetingTitle: config.title})}>
                                    Rules
                                </Button>
                                <Button responsive
                                    key='edit'
                                    disabled={isConfigPending || isBidderPending}
                                    leftIcon={<i className='fa fa-edit'/>}
                                    onClick={() => editPreTargeting.show({preTargeting: configResult.resource})}>
                                    Edit
                                </Button>
                            </>}>
                                <DetailsView fields={[
                                    {label: 'Name', value: config.title},
                                    {label: 'Active', value: renderBoolean(config.active)},
                                    {label: 'Priority', value: config.priority},
                                    {label: 'Allow Cache', value: renderBoolean(config.allowCache)},
                                    {label: 'Allow Close Delay', value: config.allowCloseDelay}
                                ]}/>
                                <Title toolbar={ability.canEdit(config) && <>
                                    <Button intent={Intent.PRIMARY}
                                        responsive
                                        disabled={variablesSource.state.isPending}
                                        onClick={() => createVariable.show({
                                            create: true,
                                            preTargetingVariable: {
                                                ...defaultPreTargetingVariable
                                            }
                                        })}
                                        leftIcon={<i className='fa fa-plus'/>}
                                    >Create</Button>
                                    <Button intent={Intent.SUCCESS} responsive
                                        key='refresh'
                                        disabled={variablesSource.state.isPending}
                                        leftIcon={<i className='fa fa-refresh'/>}
                                        onClick={() => variablesSource.fetchRecords(config.id)}>
                                        Refresh</Button>
                                </>}>Variables</Title>
                                <TableSort header
                                    columns={[{
                                        header: 'Name',
                                        field: 'name'
                                    }, {
                                        small: true,
                                        cell: variable => (
                                            <TableRowActions>
                                                {ability.canEdit(config) && <Button
                                                    small
                                                    responsive
                                                    leftIcon={<i className='fa fa-edit'/>}
                                                    onClick={() =>
                                                        editVariable.show({preTargetingVariable: variable})
                                                    }
                                                >Edit</Button>}
                                                {ability.canDelete(config) && <Button
                                                    small
                                                    responsive
                                                    leftIcon={<i
                                                        className='fa fa-trash'/>}
                                                    intent={Intent.DANGER}
                                                    onClick={() => confirm('Delete variable?', {
                                                        onOk: () => variablesSource.deleteRecord(variable),
                                                        intent: Intent.DANGER,
                                                        icon: <i
                                                            className='fa fa-trash'/>
                                                    })}
                                                >Delete</Button>}
                                            </TableRowActions>
                                        )
                                    }]}
                                    records={variablesSource.state.records}
                                    placeholder={variablesSource.state.isPending && <Progress/>}
                                />
                            </BreadcrumbsResourcePage>
                        </Breadcrumb>}
                    </ResourceLoader>
                </Breadcrumb>
            </Breadcrumb>}
    </ResourceLoader>;
};
